"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Step = {
  number: string;
  title: string;
  duration: string;
  description: string;
  offset: string;
};
const steps: Step[] = [{
  number: "01",
  title: "Sourced at Sea",
  duration: "1965–Present",
  description: "Every catch begins on the deck of the Miss Yvonne. We honor the heritage of our 1965 vessel, selecting only the finest wild-caught seafood from the Gulf.",
  offset: "md:ml-0"
}, {
  number: "02",
  title: "Expertly Hand-Cut",
  duration: "Daily",
  description: "Our master fishmongers process each fillet by hand. We believe in the precision of the blade and the integrity of the cut, ensuring zero waste and maximum quality.",
  offset: "md:ml-8"
}, {
  number: "03",
  title: "Flash-Frozen Fresh",
  duration: "Immediate",
  description: "To lock in the ocean's flavor, we flash-freeze our catch within hours of landing. This preserves the texture and nutrients as if it were just pulled from the water.",
  offset: "md:ml-2"
}, {
  number: "04",
  title: "Carefully Packed",
  duration: "Custom",
  description: "Your order is nestled into insulated, eco-friendly packaging. We treat every box like a precious cargo, ensuring it arrives in pristine condition at your doorstep.",
  offset: "md:ml-10"
}, {
  number: "05",
  title: "Swift Delivery",
  duration: "Direct",
  description: "From our docks to your kitchen, we bypass the middleman. Our logistics network ensures your seafood travels the shortest distance possible for peak freshness.",
  offset: "md:ml-4"
}, {
  number: "06",
  title: "Ready to Serve",
  duration: "Enjoy",
  description: "Whether it's a simple sear or a family feast, you are serving a piece of history. Built by hand, proven by time, and delivered with care.",
  offset: "md:ml-6"
}];
function StepCard({
  step,
  index
}: {
  step: Step;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: "0px 0px -60px 0px"
  });
  return <div data-index={index} ref={ref} className={`relative pl-20 sm:pl-28 md:pl-40 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: isVisible ? "120ms" : "0ms"
  }}>
      <div className="absolute left-0 top-0 flex items-start justify-center w-16 sm:w-24 md:w-32" aria-hidden="true">
        <span className="font-serif italic text-primary leading-none" style={{
        fontSize: "clamp(3rem, 6vw, 4rem)"
      }}>
          {step.number}
        </span>
      </div>

      <div className={`relative bg-card text-card-foreground border border-border p-8 sm:p-10 md:p-12 ${step.offset}`}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <h3 className="font-serif font-default text-2xl sm:text-3xl text-foreground leading-tight" style={{
          fontSize: "clamp(1.5rem, 2.4vw, 1.75rem)"
        }}>
            {step.title}
          </h3>
          <span className="font-mono uppercase tracking-widest text-secondary text-xs sm:text-sm whitespace-nowrap">
            {step.duration}
          </span>
        </div>
        <p className="text-base text-muted-foreground leading-relaxed font-sans">
          {step.description}
        </p>
      </div>
    </div>;
}
export default function TheShoreToDoorProcess() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const lineReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05,
    rootMargin: "0px 0px -100px 0px"
  });
  const footnoteReveal = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.4
  });
  return <section data-section-id="981" id="the-shore-to-door-process" className="relative bg-background text-foreground py-24 md:py-36 overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute -top-20 -left-24 w-96 h-96 rounded-lg bg-primary/5 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-10 -right-32 w-[28rem] h-[28rem] rounded-lg bg-secondary/5 blur-3xl" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-10">
        <div ref={headerReveal.ref} className={`text-center max-w-3xl mx-auto mb-20 md:mb-28 transition-all duration-700 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono uppercase tracking-[0.25em] text-xs sm:text-sm text-primary mb-6">
            From Shore to Door
          </p>
          <h2 className="font-serif font-default text-foreground leading-[1.05] tracking-tight" style={{
          fontSize: "clamp(2.25rem, 5vw, 3.5rem)"
        }}>
            The journey of our catch.
          </h2>
          <div className="mt-8 mx-auto h-px w-16 bg-primary/40" />
        </div>

        <div ref={lineReveal.ref} className="relative">
          <div aria-hidden="true" className="absolute top-2 bottom-2 left-8 sm:left-12 md:left-16 w-px">
            <div className="absolute inset-0 origin-top transition-transform ease-out" style={{
            transform: lineReveal.isVisible ? "scaleY(1)" : "scaleY(0)",
            transitionDuration: "1800ms",
            backgroundImage: "repeating-linear-gradient(to bottom, var(--primary) 0 6px, transparent 6px 14px)"
          }} />
          </div>

          <div className="flex flex-col gap-16 md:gap-20">
            {steps.map((step, i) => <StepCard key={step.number} step={step} index={i} />)}
          </div>
        </div>

        <p ref={footnoteReveal.ref} className={`mt-20 md:mt-28 text-center font-serif italic text-muted-foreground text-base sm:text-lg transition-all duration-700 ease-out ${footnoteReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          Every step is a testament to our commitment to quality and sustainability.
        </p>
      </div>
    </section>;
}